/**
 * Администрирование площадки (только роль admin; администратор не играет):
 *   «Обзор» — пользователи, розыгрыши, кредиты, столы, динамика по дням, лучшие игроки;
 *   «Пользователи» — поиск, фильтры, переход в профиль, блокировка и разблокировка, сброс баланса;
 *   «Розыгрыши» — все розыгрыши с переходом к повтору.
 */
import { useEffect, useState } from 'react';
import type { AdminUserRow } from '@mt/protocol';
import { t, tp } from '../../i18n';
import { api, useLoad } from '../../lib/api';
import { cr, crn } from '../../lib/format';
import { variantOfCategory, variantTitle } from '../../lib/variants';
import { Link, navigate } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { ProviderIcon } from '../auth/ProviderIcon';
import { ago, Avatar, DayBars, Empty, Pager, pct, signedCr, Tile, toneOf } from '../kit';
import { GameList } from '../profile/GameList';
import { ResetBalanceButton } from '../profile/ResetBalance';

type Tab = 'overview' | 'users' | 'games';
const TAB_IDS: readonly Tab[] = ['overview', 'users', 'games'];
/** Название вкладки — вычисляется при отрисовке, чтобы следовать текущему языку. */
const tabTitle = (id: Tab): string => t(`admin.tab.${id}`);

export function AdminPage() {
  const login = useApp((s) => s.adminLogin);
  const initial = new URLSearchParams(location.search).get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(initial && TAB_IDS.includes(initial) ? initial : 'overview');
  const choose = (t: Tab) => {
    setTab(t);
    history.replaceState(null, '', t === 'overview' ? '/admin' : `/admin?tab=${t}`);
  };
  return (
    <div className="page admin-page">
      <header className="page-head">
        <div>
          <h1>{t('admin.title')}</h1>
          <p className="muted-line">
            {t('admin.signedIn')} {login ? <b>{login}</b> : null}. {t('admin.noPlay')}
          </p>
        </div>
      </header>
      <div className="segmented tabs" role="tablist">
        {TAB_IDS.map((id) => (
          <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => choose(id)}>
            {tabTitle(id)}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'users' && <Users />}
      {tab === 'games' && (
        <section className="panel">
          <GameList mode="admin" load={(o, l) => api.admin.games(o, l)} replayPath={(id) => `/admin/games/${id}`} pageSize={30} />
        </section>
      )}
    </div>
  );
}

function Overview() {
  const info = useApp((s) => s.info);
  const { data: o, error, reload } = useLoad(() => api.admin.overview(), []);
  useEffect(() => {
    const id = setInterval(reload, 15_000);
    return () => clearInterval(id);
  }, [reload]);
  if (error) return <p className="notice danger">{error}</p>;
  if (!o) return <p className="muted-line">{t('common.loading')}</p>;
  return (
    <div className="admin-overview">
      {info && (
        <section className="panel">
          <h3 className="panel-title">{t('admin.data.title')}</h3>
          <dl className="tiles">
            <Tile
              label={t('admin.data.mode')}
              value={info.mode === 'replay' ? t('admin.data.modeReplay') : t('admin.data.modeLive')}
              hint={info.mode === 'replay' ? t('admin.data.modeReplayHint') : undefined}
            />
            <Tile label={t('admin.data.source')} value={info.source === 'alpaca' ? 'Alpaca · IEX' : t('admin.data.sourceSynthetic')} tone={info.source === 'alpaca' ? '' : 'down'} />
          </dl>
          {info.sourceNote && <p className="notice warn">{info.sourceNote}</p>}
        </section>
      )}
      <section className="panel">
        <h3 className="panel-title">{tabTitle('users')}</h3>
        <dl className="tiles">
          <Tile label={t('admin.users.total')} value={o.users.total} />
          <Tile label={t('admin.users.online')} value={o.users.online} tone={o.users.online ? 'up' : ''} />
          <Tile label={t('admin.users.new24h')} value={o.users.new24h} />
          <Tile label={t('admin.users.new7d')} value={o.users.new7d} />
          <Tile label={t('admin.users.active7d')} value={o.users.active7d} />
          <Tile label={t('admin.users.banned')} value={o.users.banned} tone={o.users.banned ? 'down' : ''} />
          <Tile label={t('admin.users.withPassword')} value={o.users.withPassword} />
          <Tile label={t('admin.users.withSso')} value={o.users.withSso} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">{tabTitle('games')}</h3>
        <dl className="tiles">
          <Tile label={t('admin.games.total')} value={o.games.total} />
          <Tile label={t('admin.games.last24h')} value={o.games.last24h} />
          <Tile label={t('admin.games.completed')} value={o.games.completed} />
          <Tile label={t('admin.games.cancelled')} value={o.games.cancelled} />
          <Tile label={t('admin.games.avgPlayers')} value={o.games.avgPlayers ?? '—'} />
          <Tile label={t('admin.games.humanShare')} value={pct(o.games.humanShare)} hint={t('admin.games.humanShareHint')} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">{t('admin.credits.title')}</h3>
        <dl className="tiles">
          <Tile label={t('admin.credits.bank')} value={crn(o.credits.bank)} hint={t('admin.credits.bankHint')} />
          <Tile label={t('admin.credits.commissionTotal')} value={crn(o.credits.commissionTotal)} />
          <Tile label={t('admin.credits.granted')} value={crn(o.credits.granted)} hint={t('admin.credits.grantedHint')} />
          <Tile label={t('admin.credits.inPlay')} value={crn(o.credits.inPlay)} hint={t('admin.credits.inPlayHint')} />
          <Tile label={t('admin.credits.potTotal')} value={crn(o.credits.potTotal)} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">{t('admin.rooms.title')}</h3>
        <dl className="tiles">
          <Tile label={t('admin.rooms.open')} value={o.rooms.open} />
          <Tile label={t('admin.rooms.playing')} value={o.rooms.playing} />
          <Tile label={t('admin.rooms.seated')} value={o.rooms.seated} />
          <Tile label={t('admin.rooms.spectators')} value={o.rooms.spectators} />
        </dl>
      </section>
      <section className="panel wide">
        <h3 className="panel-title">{t('admin.daily.games')}</h3>
        <DayBars items={o.daily} value={(i) => o.daily[i]!.games} label={t('admin.daily.games')} signed={false} format={(v) => tp('admin.daily.gamesValue', v)} />
        <h3 className="panel-title">{t('admin.daily.commission')}</h3>
        <DayBars items={o.daily} value={(i) => o.daily[i]!.commission} label={t('admin.daily.commission')} signed={false} format={(v) => cr(v)} />
      </section>
      <section className="panel">
        <h3 className="panel-title">{t('admin.byTable.title')}</h3>
        {o.byCategory.length === 0 ? (
          <Empty>{t('admin.byTable.empty')}</Empty>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('admin.byTable.ante')}</th>
                <th className="num">{t('admin.byTable.games')}</th>
                <th className="num">{t('admin.byTable.pot')}</th>
                <th className="num">{t('admin.byTable.commission')}</th>
              </tr>
            </thead>
            <tbody>
              {o.byCategory.map((c) => (
                <tr key={c.categoryId}>
                  <td data-label={t('admin.byTable.ante')}>
                    {cr(c.ante)}
                    {(() => {
                      const v = variantOfCategory(info, c.categoryId);
                      return v ? <span className="dim"> · {variantTitle(v)}</span> : null;
                    })()}
                  </td>
                  <td data-label={t('admin.byTable.games')} className="num mono">{c.games}</td>
                  <td data-label={t('admin.byTable.pot')} className="num mono">{crn(c.pot)}</td>
                  <td data-label={t('admin.byTable.commission')} className="num mono">{crn(c.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="panel">
        <h3 className="panel-title">{t('admin.top.title')}</h3>
        {o.topPlayers.length === 0 ? (
          <Empty>{t('admin.top.empty')}</Empty>
        ) : (
          <ol className="top-list">
            {o.topPlayers.map((p) => (
              <li key={p.id}>
                <Link to={`/admin/users/${p.id}`}>{p.name}</Link>
                <span className="dim small">
                  {tp('admin.top.games', p.games)} · {pct(p.winRate)}
                </span>
                <b className={`mono ${toneOf(p.net)}`}>{signedCr(p.net)}</b>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function Users() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'online' | 'banned'>('all');
  const [offset, setOffset] = useState(0);
  const { data, error, reload } = useLoad(() => api.admin.users(query, status, offset), [query, status, offset]);
  useEffect(() => {
    const id = setTimeout(() => (setQuery(q.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [q]);
  const toggleBan = async (u: AdminUserRow) => {
    try {
      if (u.bannedAt) await api.admin.unban(u.id);
      else {
        const reason = prompt(t('admin.list.banPrompt', { name: u.name }), '');
        if (reason === null) return;
        await api.admin.ban(u.id, reason);
      }
      reload();
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };
  return (
    <section className="panel">
      <div className="toolbar">
        <input className="search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('admin.list.searchPlaceholder')} aria-label={t('admin.list.searchLabel')} />
        <div className="segmented three">
          {(['all', 'online', 'banned'] as const).map((s) => (
            <button key={s} className={status === s ? 'on' : ''} onClick={() => (setStatus(s), setOffset(0))}>
              {s === 'all' ? t('admin.list.filterAll') : s === 'online' ? t('admin.list.filterOnline') : t('admin.list.filterBanned')}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="notice danger">{error}</p>}
      {data && data.total === 0 && <Empty>{t('admin.list.empty')}</Empty>}
      {data && data.total > 0 && (
        <>
          <div className="table-scroll">
            <table className="data-table users-table">
              <thead>
                <tr>
                  <th>{t('admin.list.colPlayer')}</th>
                  <th>{t('admin.list.colLogin')}</th>
                  <th>{t('admin.list.colActivity')}</th>
                  <th className="num">{t('admin.list.colGames')}</th>
                  <th className="num">{t('admin.list.colWinRate')}</th>
                  <th className="num">{t('admin.list.colNet')}</th>
                  <th className="num">{t('admin.list.colBalance')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} className={u.bannedAt ? 'banned' : ''}>
                    <td data-label={t('admin.list.colPlayer')}>
                      <span className="user-cell">
                        <Avatar id={u.id} name={u.name} size={28} />
                        <span>
                          <Link to={`/admin/users/${u.id}`}>{u.name}</Link>
                          {u.online && <em className="tag ok">{t('admin.list.tagOnline')}</em>}
                          {u.bannedAt && <em className="tag danger" title={u.banReason ?? ''}>
                              {t('admin.list.tagBanned')}
                            </em>}
                        </span>
                      </span>
                    </td>
                    <td data-label={t('admin.list.colLogin')}>
                      <span className="methods">
                        {u.login && <span className="chip-static">{u.login}</span>}
                        {u.providers.map((p) => (
                          <ProviderIcon key={p} id={p} />
                        ))}
                        {!u.login && u.providers.length === 0 && <span className="dim small">{t('admin.list.sessionOnly')}</span>}
                      </span>
                    </td>
                    <td data-label={t('admin.list.colActivity')} className="small">
                      {u.online ? t('admin.list.now') : ago(u.lastSeenAt)}
                    </td>
                    <td data-label={t('admin.list.colGames')} className="num mono">{u.games}</td>
                    <td data-label={t('admin.list.colWinRate')} className="num mono">{pct(u.winRate)}</td>
                    <td data-label={t('admin.list.colNet')} className={`num mono ${toneOf(u.net)}`}>{signedCr(u.net)}</td>
                    <td data-label={t('admin.list.colBalance')} className="num mono">{crn(u.balance)}</td>
                    <td className="num row-action">
                      <span className="row-actions">
                        <button className="btn tiny ghost" onClick={() => navigate(`/admin/users/${u.id}`)}>
                          {t('admin.list.profile')}
                        </button>
                        <ResetBalanceButton name={u.name} balance={u.balance} reset={() => api.admin.resetBalance(u.id)} onDone={reload} />
                        <button className={`btn tiny ${u.bannedAt ? '' : 'danger-outline'}`} onClick={() => void toggleBan(u)}>
                          {u.bannedAt ? t('admin.list.unban') : t('admin.list.ban')}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={data.offset} limit={data.limit} total={data.total} onPage={setOffset} />
        </>
      )}
    </section>
  );
}
